import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Signed-out landing preview — a concrete "what your Boardroom looks like
 * after four sessions" carousel. Static content by design: no fetches, no
 * personalization, just a compounding growth story any first-time visitor
 * can see themselves in.
 *
 * Mount BELOW HowItWorksSection so visitors see the abstract ladder first,
 * then the concrete future.
 */
const JOURNEY = [
  {
    week: "Week 1",
    title: "The first sit-down",
    wins: ["Named 3 goals for the quarter", "Committed to a Friday cadence"],
    dodges: [],
    close: "You wrote the rules of your own boardroom.",
  },
  {
    week: "Week 2",
    title: "First wins, first dodge",
    wins: ["Shipped auth flow (goal #1)"],
    dodges: ["Delayed the marketing push again"],
    close: "The pattern is on the record now.",
  },
  {
    week: "Week 3",
    title: "Breakthrough",
    wins: ["Marketing push launched", "Third goal moving"],
    dodges: [],
    close: "Two weeks ago you would've dodged this.",
  },
  {
    week: "Week 4",
    title: "First month closed",
    wins: ["3 of 3 goals in motion", "Cadence held every session"],
    dodges: [],
    close: "This is what compounding looks like.",
  },
];

export default function SampleJourney() {
  return (
    <section
      className="surface rounded-md p-6 sm:p-8"
      data-testid="sample-journey-section"
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#c89f45]" />
            <p className="label-eyebrow">A month on the record</p>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3 max-w-2xl">
            What your Boardroom looks like after four sessions.
          </h2>
          <p className="text-sm text-[#888] mt-3 max-w-xl">
            Not a screenshot. A story. Every card below is a real kind of session — the
            wins named, the dodges named, the pattern surfaced.
          </p>
        </div>
        <Link
          to="/sample"
          className="text-xs font-mono uppercase tracking-widest text-[#c89f45] hover:text-[#e0bd6a] transition-colors inline-flex items-center gap-1 shrink-0"
          data-testid="sample-journey-see-full"
        >
          See a full session
          <ArrowUpRight size={12} />
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sample-journey-grid">
        {JOURNEY.map((step, i) => (
          <article
            key={step.week}
            className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-md p-5 hover:border-[#c89f45]/40 transition-colors relative"
            data-testid={`sample-journey-card-${i}`}
          >
            <div className="flex items-center justify-between">
              <span className="label-eyebrow text-[#c89f45]">{step.week}</span>
              <span className="text-[10px] font-mono text-[#7a7a7a]">
                {String(i + 1).padStart(2, "0")} / 04
              </span>
            </div>
            <h3 className="font-serif text-xl mt-3 leading-snug">{step.title}</h3>

            {step.wins.length > 0 && (
              <ul className="mt-4 space-y-1.5">
                {step.wins.map((w) => (
                  <li key={w} className="flex items-start gap-2 text-xs text-[#bbb]">
                    <CheckCircle2 size={11} className="text-[#9bd3c4] mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}

            {step.dodges.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {step.dodges.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-xs text-[#bbb]">
                    <AlertCircle size={11} className="text-[#e07b6a] mt-0.5 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            )}

            <p className="mt-5 pt-4 border-t border-[#1a1a1a] text-[11px] italic text-[#888] leading-relaxed">
              "{step.close}"
            </p>
          </article>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-3 pt-6 border-t border-[#1a1a1a]">
        <p className="text-sm text-[#888] max-w-xl">
          After 12 sessions, the AI can answer questions across every single one — <em className="text-[#bbb]">"What have I been putting off for three weeks?"</em> — grounded in your own transcripts.
        </p>
        <Link
          to="/meetings/new"
          data-testid="sample-journey-cta-start"
          className="text-xs font-mono uppercase tracking-widest text-[#c89f45] hover:text-[#e0bd6a] inline-flex items-center gap-1"
        >
          Start Week 1
          <ArrowUpRight size={12} />
        </Link>
      </div>
    </section>
  );
}
